import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './store/authStore';
import pushService from './services/pushService';
import offlineDB from './utils/offlineDB';
import dataCache from './services/dataCache';
import connectivity from './utils/connectivity';
import Layout from './components/Layout';
import Login from './pages/Login';
import Home from './pages/Home';
import Instalaciones from './pages/Instalaciones';
import Empleados from './pages/Empleados';
import NotaGastos from './pages/NotaGastos';
import MiPerfil from './pages/MiPerfil';
import Calendario from './pages/Calendario';
import Partes         from './pages/Partes';
import Actas          from './pages/Actas';
import Planificacion  from './pages/Planificacion';
import Nominas        from './pages/Nominas';
import AdminDocs      from './pages/AdminDocs';
import Endesa         from './pages/Endesa';
import Prejob         from './pages/Prejob';
import PrejobSegavan  from './pages/PrejobSegavan';
import Inventario     from './pages/Inventario';
import Coches         from './pages/Coches';
import Fichajes       from './pages/Fichajes';
import Vacaciones     from './pages/Vacaciones';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];

function Private({ children }) {
  const token = useAuthStore(s => s.token);
  return token ? <Layout>{children}</Layout> : <Navigate to="/login" replace />;
}

function OnlyRoles({ roles, children }) {
  const user = useAuthStore(s => s.user);
  if (!user) return null;
  return roles.includes(user.rol) ? children : <Navigate to="/" replace />;
}

export default function App() {
  const fetchMe = useAuthStore(s => s.fetchMe);
  const token   = useAuthStore(s => s.token);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Cuando hay sesión: sincronizar token al SW, activar push y pre-cargar datos
  useEffect(() => {
    if (!token) return;

    // Guardar token en IndexedDB para que el SW lo use en background sync
    offlineDB.setMeta('token', token).catch(() => {});

    // Notificar al SW activo del nuevo token
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SET_TOKEN', token });
    }

    // Iniciar push (si ya tenía permiso, re-suscribe silenciosamente)
    pushService.init().catch(() => {});

    // Pre-cargar datos clave para uso offline
    dataCache.preFetch().catch(() => {});

    // Al reconectar: volver a pre-cargar (forzado) para tener datos frescos
    const unsub = connectivity.subscribe(({ online, event }) => {
      if (online && event === 'reconnected') {
        dataCache.preFetch(true).catch(() => {});
      }
    });
    return unsub;
  }, [token]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/"              element={<Private><Home /></Private>} />
        <Route path="/instalaciones" element={<Private><Instalaciones /></Private>} />
        <Route path="/empleados"     element={<Private><Empleados /></Private>} />
        <Route path="/partes"        element={<Private><Partes /></Private>} />
        <Route path="/ptw"           element={<Navigate to="/documentos" replace />} />
        <Route path="/prejob"        element={<Private><Prejob /></Private>} />
        <Route path="/prejob-segavan" element={<Private><OnlyRoles roles={['admin','tecnico']}><PrejobSegavan /></OnlyRoles></Private>} />
        <Route path="/gastos"        element={<Private><NotaGastos /></Private>} />
        <Route path="/documentos"    element={<Navigate to="/endesa" replace />} />
        <Route path="/fichaje"       element={<Navigate to="/calendario" replace />} />
        <Route path="/fichajes"      element={<Private><OnlyRoles roles={ROLES_ADMIN}><Fichajes /></OnlyRoles></Private>} />
        <Route path="/calendario"    element={<Private><Calendario /></Private>} />
        <Route path="/vacaciones"    element={<Private><OnlyRoles roles={ROLES_ADMIN}><Vacaciones /></OnlyRoles></Private>} />
        <Route path="/actas"         element={<Private><Actas /></Private>} />
        <Route path="/planificacion" element={<Private><Planificacion /></Private>} />
        <Route path="/nominas"       element={<Private><Nominas /></Private>} />
        <Route path="/admin-docs"    element={<Private><AdminDocs /></Private>} />
        <Route path="/endesa"        element={<Private><Endesa /></Private>} />
        <Route path="/inventario"    element={<Private><Inventario /></Private>} />
        <Route path="/coches"        element={<Private><Coches /></Private>} />
        <Route path="/perfil"        element={<Private><MiPerfil /></Private>} />
        <Route path="*"              element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
